import { BOARD_IMPORT_HANDLER } from '../creation/new-board/new-board.component';
import { boardImportHandlerFactory } from '../../files/board-import.service';
import { Component, computed, DestroyRef, effect, inject, Injector, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { FullWindowService, injectFullWindowStart } from '@modules/common/services/full-window.service';
import { Application } from '@modules/common/types/application';
import { CollapsibleRailShellComponent, saveRailDocked } from '@modules/pages/components/collapsible-rail-shell/collapsible-rail-shell.component';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { openDeckEditor } from '@modules/slides/utils/open-deck-editor.util';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { BoardContentFlushService } from '../../services/board-content-flush.service';
import { reconcileEditorLifecycle } from '../../services/boards-editor-lifecycle';
import { createMetadataGate } from '../../services/boards-metadata-gate';
import { BoardListStore } from '../../services/board-list.store';
import { BOARDS_SEARCH_STATE_PROVIDER } from '../../services/boards-search-state';
import { BoardsEditorStore } from '../../services/boards-editor.store';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { BoardsWindowService } from '../../services/boards-window.service';
import { BoardCommentRequestsService } from '../../services/board-comment-requests.service';
import { BoardsPanelHandoffService, type BoardsHandoff, type BoardsHandoffReason } from '../../services/boards-panel-handoff.service';
import { isTempBoard, BoardsStore } from '../../services/boards.store';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { ApplicationScope } from '@modules/common/types/ApplicationScope';
import { BoardKnowledgeVisibilityService } from '../../knowledge/board-knowledge-visibility.service';
import { BoardsItemTabs } from '../../ui-commands/boards-item-tabs';
import { BoardsUiSession, type BoardsUiHost } from '../../ui-commands/boards-ui-session';
import { BoardsPreviewComponent } from '../library/boards-preview/boards-preview.component';
import { BOARD_OPENER, type BoardOpener } from '../shared/board-opener';

/** The preview list's `magneticSize` in the template: the narrowest it opens at. */
export const BOARDS_LIST_MAGNETIC_SIZE = 300;
/** The list beside the preview and the one beside the open editor keep their own widths and fold states (document24). */
export const BOARDS_LIST_KEY = 'boardsList';
export const BOARDS_EDITOR_LIST_KEY = 'boardsEditorList';
/** Native widths: the DP's reopen floor, and the editor-list's. */
const DP_KEY = 'dp';
const DP_MAGNETIC_SIZE = 200;
/** The longest a hand-off waits for the collection it needs to load (bounded work). */
export const HANDOFF_READY_WAIT_MS = 15_000;

/** The one opener every creation and every Open editor uses: the guarded open, starting the session only on success. */
export const STILL_CREATING_MESSAGE = 'This board is still being created. Try again in a moment.';

/**
 * The library shows a new board at once under a client temp id and swaps in the server's on the receipt. The editor reads
 * the server, so a temp id that has not been swapped yet has nothing to open: the id is resolved first, and one still
 * unsettled is refused (never sent to the server, never folding anything).
 */
export function settledBoardId(boards: Pick<InstanceType<typeof BoardsStore>, 'resolveBoardId'> | null, boardId: string): string | null {
  const id = boards?.resolveBoardId(boardId) ?? boardId;
  return isTempBoard(id) ? null : id;
}

/**
 * Every editor entry leaves the item the library is showing, so the owning Details leave guard (an unsaved draft) is asked
 * first, for double-click, Open editor and every creation alike (`BoardsStore.canLeave`, the check `BoardsPreviewComponent`
 * makes for its own button). A veto leaves the item, its draft and the panels exactly as they were (R04-CX-6).
 */
export function mayLeaveItem(boards: { canLeave(boardId: string | undefined): boolean; selectedBoardId(): string | null } | null): boolean {
  return !boards || boards.canLeave(boards.selectedBoardId() ?? undefined);
}

export function boardOpenerFactory(): BoardOpener {
  const store = inject(BoardsEditorStore);
  const boards = inject(BoardsStore, { optional: true });
  const toaster = inject(ToasterService);
  const start = injectFullWindowStart(Application.boards);
  return (boardId, options) => {
    const id = settledBoardId(boards, boardId);
    if (id === null) toaster.show({ text: STILL_CREATING_MESSAGE });
    else if (mayLeaveItem(boards)) {
      // The annotation to show rides the same open: the editor focuses it once its canvas is ready (R05-CX-81).
      if (options?.annotationThreadUid) store.requestAnnotationFocus(id, options.annotationThreadUid);
      openDeckEditor(store, id, start); // Slides' own guarded opener (open-deck-editor.util.ts), reused
    }
  };
}

/**
 * The routed `/boards` page, a copy of `SlidesLayoutComponent` (pages/components/slides-layout) for the three-panel
 * shell: rail, list, and item or editor, with the Dynamic Panel outside it.
 *
 * Deliberately NOT standalone, for the reason Slides gives: a routed standalone component gets its own environment
 * injector holding every provider of the NgModules reachable from its imports, which would give Boards private copies of
 * `SplitViewService` and `DragNDropService`. It is declared in `PagesModule` and shares that module's instances.
 *
 * What differs from Slides: no deep-link or hand-off consumer yet (R05), the rail carries an opt-in divider, and the
 * editor's identity lives in `BoardsEditorStore` while the library's selection lives in the slot's `BoardsStore`; the
 * effects below keep the two in step (the editor store is the only writer of "which board is open").
 */
@Component({
  selector: 'app-boards-layout',
  templateUrl: './boards-layout.component.html',
  styleUrls: ['./boards-layout.component.less', '../../../pages/styles.less'],
  standalone: false,
  providers: [BoardListStore, BOARDS_SEARCH_STATE_PROVIDER, { provide: BOARD_OPENER, useFactory: boardOpenerFactory }, { provide: BOARD_IMPORT_HANDLER, useFactory: boardImportHandlerFactory }],
})
export class BoardsLayoutComponent {
  protected readonly app = Application.boards;
  protected readonly editorStore = inject(BoardsEditorStore);
  protected readonly boards = inject(BoardsStore);
  private readonly listStore = inject(BoardListStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly splitView = inject(SplitViewService);
  private readonly uiSession = inject(BoardsUiSession);
  private readonly itemTabs = inject(BoardsItemTabs);
  private readonly toaster = inject(ToasterService);
  private readonly handoff = inject(BoardsPanelHandoffService);
  private readonly contentFlush = inject(BoardContentFlushService);
  private readonly spaceContext = inject(SpaceContextStore);
  /** SBUX-1144: Open editor and every new board made here open the editor full-window (`FullWindowService`). */
  protected readonly fullWindow = inject(FullWindowService);
  protected readonly railFolded = computed(() => this.fullWindow.railFoldedApp() === this.app);
  protected readonly railShell = viewChild(CollapsibleRailShellComponent);
  private readonly dpFoldedSaved = toSignal(this.splitView.getMinimized(DP_KEY), { initialValue: false });
  /** The effective 4th-panel state (saved fold and the full-window fold), for the editor's Frames drawer width. */
  protected readonly dpOpen = computed(() => !this.dpFoldedSaved() && !this.fullWindow.dpFolded());
  private readonly preview = viewChild(BoardsPreviewComponent);

  protected readonly folderId = signal<string | null>(null);
  protected readonly previewFolders = computed(() => {
    if (this.boards.selectedBoardId()) return [];
    return this.boards.folders().filter((folder) => folder.id === this.folderId());
  });
  protected readonly folderSelectAllRequest = signal(0);
  private folderNavigation = 0;
  private appliedHandoff: BoardsHandoff | null = null;

  /** Whether the `boardsList` pane is collapsed to its 25 px nub, passed in rather than read by the list itself (Slides, same reason). */
  protected readonly listMinimized = toSignal(this.splitView.getMinimized(BOARDS_LIST_KEY), { initialValue: false });
  /**
   * Beside an open editor the list starts folded to its edge tab, and once the user opens it, it stays open while the
   * editor moves between boards. A new full-window open folds it again (Slides, Wook 2026-10-27: "Yes, add a list toggle").
   */
  protected readonly editorListOpen = signal(false);
  protected readonly editorListMinimized = computed(() => !this.editorListOpen() || this.editorListSaved());
  private readonly editorListSaved = toSignal(this.splitView.getMinimized(BOARDS_EDITOR_LIST_KEY), { initialValue: false });

  public constructor() {
    this.destroyRef.onDestroy(this.boards.attachView());
    // A session starts only from a trigger's own open (`FullWindowStart`), while this page is on screen; it ends when the
    // editor closes or this page goes away. Switching boards inside the editor changes neither.
    this.destroyRef.onDestroy(this.fullWindow.attach(this.app));
    let editorBoardId: string | null = null;
    effect(() => {
      const boardId = this.editorStore.editorBoardId();
      untracked(() => {
        // Every editor entry, with or without a session, starts with the editor-side list folded.
        if (boardId !== null && editorBoardId === null) this.editorListOpen.set(false);
        if (boardId === null && editorBoardId !== null) this.endFullWindow();
        editorBoardId = boardId;
      });
    });
    effect(() => {
      this.fullWindow.starts();
      untracked(() => this.editorListOpen.set(false));
    });
    this.destroyRef.onDestroy(() => this.endFullWindow());
    this.keepLibraryInStep();
    // A record asked for by the Dynamic Panel or a linked item (or the panel's own Open editor): the request keeps its identity until this page
    // completes it, is applied once, and is re-checked after every wait (slides-layout.component.ts:317-340).
    effect(() => {
      const request = this.handoff.pending();
      if (!request || request === this.appliedHandoff) return;
      this.appliedHandoff = request;
      untracked(() => void this.applyHandoff(request));
    });
    // A request still pending when the page goes away belongs to this visit; it must not replay on the next one.
    this.destroyRef.onDestroy(() => this.handoff.withdrawPending());
    this.destroyRef.onDestroy(this.uiSession.registerHost(this.uiHost()));
  }

  /**
   * The editor store decides which board is open; the library store (selection, list surfaces) is told. A board that
   * the editor opens becomes the selected item; a list surface closing the editor (`BoardsStore.closeEditor`) or a
   * permanent delete asks the editor store to close it; a board moved to Trash goes read-only in place.
   */
  private keepLibraryInStep(): void {
    effect(() => {
      const open = this.editorStore.editorBoardId();
      untracked(() => {
        this.boards.setEditorBoard(open);
        if (open !== null && this.boards.selectedBoardId() !== open) this.boards.selectBoard(open);
      });
    });
    let mirrored: string | null = null;
    effect(() => {
      const libraryOpen = this.boards.editorBoardId();
      untracked(() => {
        const open = this.editorStore.editorBoardId();
        if (libraryOpen === null && mirrored !== null && open === mirrored) {
          if (this.boards.boards().some((board) => board.id === mirrored)) void this.editorStore.closeEditor();
          else this.editorStore.boardDeleted(mirrored);
        }
        mirrored = libraryOpen;
      });
    });
    // The library's optimistic Trash both ways, and a permanent delete, for the open board (R04-CX-4); the window does the same over its own store.
    reconcileEditorLifecycle(this.editorStore, this.boards, this.injector);
    // A guarded exit waits for the board's own metadata writes (a rename) before it saves the scene (R04-CX-3): the same drain every
    // editor scope installs.
    const gate = createMetadataGate(this.boards);
    this.editorStore.setMetadataGate(gate);
    // Copies made from this page's library (Duplicate, Copy To, Save as template) save the open board's pending scene first (R04-CX-20).
    this.destroyRef.onDestroy(this.contentFlush.register(
      (boardId) => (boardId === undefined || this.editorStore.editorBoardId() === boardId ? this.editorStore.flushCurrent() : true),
      (boardId) => this.editorStore.hasUnsavedWork(boardId),
      this.spaceContext, // owner: the departure of THIS space context reaches only its own editors (R04-CX-40)
    ));
    this.destroyRef.onDestroy(() => this.editorStore.setMetadataGate(null));
    // Asked again right before an open's final commit, so a Details draft begun while the scene loaded is not destroyed (R04-CX-6).
    this.editorStore.setEntryGuard(() => mayLeaveItem(this.boards));
    this.destroyRef.onDestroy(() => this.editorStore.setEntryGuard(null));
  }

  /**
   * A full-window session's end reopens the rail and the list and saves them open, whatever they were; the Dynamic Panel
   * is the shell layout's. A sessionless editor closes writing nothing (Slides, slides-layout.component.ts:371-382).
   */
  private endFullWindow(): void {
    if (!this.fullWindow.end(this.app)) return;
    const rail = this.railShell();
    if (rail) rail.dock();
    else saveRailDocked(this.app);
    this.splitView.reopen(BOARDS_LIST_KEY, BOARDS_LIST_MAGNETIC_SIZE);
  }

  /**
   * `fromFoldersList`: picked on the list's "Folders" view, which stays as it is, so the next folder is one click away;
   * from the sidebar the list narrows to the folder's boards (slides-layout.component.ts:260-275). An open editor is
   * closed first, saving it; any refusal leaves everything where it was.
   */
  protected async openFolder(id: string, selectAll = false, fromFoldersList = false, isCurrent: () => boolean = () => true): Promise<boolean> {
    if (!this.boards.folders().some((folder) => folder.id === id)) return false;
    const generation = ++this.folderNavigation;
    if (this.editorStore.editorBoardId() && !(await this.editorStore.closeEditor())) return false;
    // Re-checked after the guarded close (slides-layout.component.ts:321-324, and the prototype's afterSavedCurrent): a newer navigation or a
    // request that is no longer the live one commits nothing.
    if (generation !== this.folderNavigation || !isCurrent() || !this.boards.folders().some((folder) => folder.id === id) || this.preview()?.canLeave() === false) return false;
    this.boards.selectBoard(null);
    if (this.boards.selectedBoardId() !== null) return false; // a draft guard refused
    if (!fromFoldersList) this.listStore.setFolder(id);
    this.folderSelectAllRequest.set(selectAll ? generation : 0);
    this.folderId.set(id);
    return true;
  }

  /**
   * One hand-off, as `applyDeepLink` / `openComments` do for Slides (slides-layout.component.ts:137-160, :317-340): wait for the target
   * collection to finish loading, do the guarded work, re-check that the request is still the live one, verify the actual target, and only
   * then commit and report. A board folder opens by its identity; a board's record is selected in the item view (the open editor is saved and
   * closed first, like opening a folder); the explicit editor intent goes through the shared guarded opener.
   */
  private async applyHandoff(request: BoardsHandoff): Promise<void> {
    // Slides cancels a hand-off on ANY workspace change and rechecks during guarded completion (`slides-layout.component.ts:203`, `:325`): the request is bound to
    // the context-change counter seen once its own switch has landed, so a switch away and back during a load can never make it current again (R10-CX-220).
    let spaceGeneration = this.spaceContext.contextChanges();
    const current = (): boolean => this.handoff.isPending(request) && this.spaceContext.contextChanges() === spaceGeneration;
    let shown = false;
    let reason: BoardsHandoffReason = 'failed';
    try {
      // Slides' layout (`slides-layout.component.ts:182-203`): a hand-off from another workspace first switches THIS page's own space context, through the
      // guarded `setContext` (unsaved-forms confirmation and a flush), then loads. A refused or superseded switch opens nothing; the source stays (R10-CX-210).
      if (request.spaceType !== undefined && (this.spaceContext.type() !== request.spaceType || this.spaceContext.contextId() !== request.spaceId)) {
        const switched = await firstValueFrom(this.spaceContext.setContext(request.spaceType, request.spaceId), { defaultValue: false });
        spaceGeneration = this.spaceContext.contextChanges(); // the request's own switch is not an intervening change
        if (!current() || !switched || this.spaceContext.type() !== request.spaceType || this.spaceContext.contextId() !== request.spaceId) {
          reason = current() ? 'vetoed' : 'superseded';
          return;
        }
      }
      if (request.kind === 'boardFolder' || request.intent === 'record') {
        // A same-space hand-off still forces a reload (slides-layout.component.ts:166-175, :200): this page's store may hold an old list, and the
        // record may have been made or changed in another slot since. The target is judged absent only after that current read; the library's
        // loadAll keeps rows with a write pending or made since it asked (decks.store.ts:3153-3200), so nothing the user has done is lost.
        this.boards.loadAll();
      }
      if (request.kind === 'boardFolder') {
        if (await this.whenReady('folders', current)) shown = await this.openFolder(request.id, false, false, current);
        reason = shown ? 'shown' : current() ? 'failed' : 'superseded';
      } else if (request.intent === 'editor') {
        {
          const opener = this.injector.get(BOARD_OPENER);
          if (request.annotationThreadUid) opener(request.id, { annotationThreadUid: request.annotationThreadUid });
          else opener(request.id);
        }
        shown = true;
        reason = 'shown'; // the opener answers through its own guards; nothing consumes a panel Open editor
      } else if (!(await this.whenReady('boards', current))) {
        reason = current() ? 'unavailable' : 'superseded';
      } else {
        reason = await this.showRecord(request.id, current);
        shown = reason === 'shown';
        // Only after the record is up and this is still the live request (Slides' `openComments` requests after navigation succeeds).
        if (shown && current() && request.tab === 'comments') {
          this.injector.get(BoardCommentRequestsService).request(request.id);
          request.onOpened?.(); // Slides: the destination opens Comments, then calls `onOpened`, which closes the source (slides-layout `openComments`).
        }
      }
    } finally {
      const live = shown && current();
      this.handoff.clear(request, live, !current() ? 'superseded' : reason === 'shown' ? 'failed' : reason);
    }
  }

  /**
   * Waits for the collection the request is about (Slides' deep link waits for \`decksLoaded && !decksLoading\` of the deck collection for a deck and
   * the folder collection for a folder, slides-layout.component.ts:140-145): an unrelated collection's load or failure neither ends nor satisfies
   * it. False if the request stopped being the live one, or the load did not finish within a bound; a failed load of THIS collection counts as
   * finished (the target is then simply absent).
   */
  private whenReady(collection: 'boards' | 'folders', current: () => boolean): Promise<boolean> {
    const finished = (): boolean =>
      collection === 'boards'
        ? (this.boards.boardsLoaded() && !this.boards.boardsLoading()) || (!this.boards.boardsLoading() && this.boards.boardsLoadError())
        : (this.boards.foldersLoaded() && !this.boards.foldersLoading()) || (!this.boards.foldersLoading() && this.boards.foldersLoadError());
    if (!current()) return Promise.resolve(false);
    if (finished()) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        ref.destroy();
        resolve(false);
      }, HANDOFF_READY_WAIT_MS);
      const ref = effect(
        () => {
          this.handoff.pending(); // wakes on supersession
          const isReady = finished();
          untracked(() => {
            if (!current()) resolve(false);
            else if (!isReady) return;
            else resolve(true);
            clearTimeout(timer);
            ref.destroy();
          });
        },
        { injector: this.injector },
      );
    });
  }

  private async showRecord(boardId: string, current: () => boolean): Promise<BoardsHandoffReason> {
    const id = settledBoardId(this.boards, boardId);
    if (id === null) {
      this.toaster.show({ text: STILL_CREATING_MESSAGE });
      return 'unavailable';
    }
    const present = (): boolean => this.boards.boards().some((board) => board.id === id && !board.deleted);
    if (!present()) {
      this.toaster.show({ text: 'This board is unavailable or you do not have access.' });
      return 'unavailable';
    }
    if (!mayLeaveItem(this.boards)) return 'vetoed';
    if (this.editorStore.editorBoardId()) {
      const left = await this.editorStore.closeEditorOutcome();
      if (left === 'vetoed' || left === 'save_failed' || left === 'superseded') return left;
    }
    // After the guarded close: still the live request, and the target still there (the prototype's afterSavedCurrent).
    if (!current()) return 'superseded';
    if (!present()) return 'unavailable';
    this.boards.selectBoard(id);
    this.folderId.set(null);
    return this.boards.selectedBoardId() === id ? 'shown' : 'vetoed';
  }

  /** A list pick beside the editor, or a double-click: the guarded switch / sessionless entry, once the board has a server id. */
  protected switchTo(boardId: string): void {
    const id = settledBoardId(this.boards, boardId);
    if (id === null) this.toaster.show({ text: STILL_CREATING_MESSAGE });
    else this.editorStore.selectBoard(id);
  }

  protected editBoard(boardId: string): void {
    const id = settledBoardId(this.boards, boardId);
    if (id === null) this.toaster.show({ text: STILL_CREATING_MESSAGE });
    else if (mayLeaveItem(this.boards)) this.editorStore.openEditor(id);
  }

  /** The folder's New board has made its board: the list shows it, and it opens through the same guarded opener as every creation. */
  protected openCreatedFolderBoard(folderId: string, boardId: string): void {
    this.listStore.setFolder(folderId);
    this.listStore.ensureVisibleAfterCreate(boardId);
    this.injector.get(BOARD_OPENER)(boardId);
  }

  /** Parks or docks the rail exactly as its own strip button does (Stitchy's set_panel_open); a full-window fold ends as the strip's button ends it. */
  private setRailOpen(open: boolean): void {
    const rail = this.railShell();
    if (!rail) return;
    if (this.railFolded()) {
      if (open) this.fullWindow.reopenRail();
      return;
    }
    const parked = rail.shownParked();
    if (parked && open) rail.dock();
    else if (!parked && !open) rail.park();
  }

  /** What Stitchy's UI commands (MP-034/035) may do to this page; every command goes through the same guarded paths as the controls. */
  private uiHost(): BoardsUiHost {
    const keyOf = (panel: 'list' | 'editorList' | 'dp'): string => (panel === 'list' ? BOARDS_LIST_KEY : panel === 'editorList' ? BOARDS_EDITOR_LIST_KEY : DP_KEY);
    const magnetic = (panel: 'list' | 'editorList' | 'dp'): number => (panel === 'dp' ? DP_MAGNETIC_SIZE : BOARDS_LIST_MAGNETIC_SIZE);
    return {
      state: () => ({
        selectedBoardId: this.boards.selectedBoardId(),
        editorBoardId: this.editorStore.editorBoardId(),
        saveStatus: this.editorStore.saveStatus(),
        fullWindow: this.fullWindow.activeApp() === this.app,
        panels: {
          rail: !(this.railShell()?.shownParked() ?? false) && !this.railFolded() ? 'open' : 'folded',
          list: this.splitView.isMinimized(BOARDS_LIST_KEY) ? 'folded' : 'open',
          editorList: this.editorListMinimized() ? 'folded' : 'open',
          dp: this.splitView.isMinimized(DP_KEY) || this.fullWindow.dpFolded() ? 'folded' : 'open',
        },
        widths: {
          list: this.splitView.size.getSync(BOARDS_LIST_KEY) ?? null,
          editorList: this.splitView.size.getSync(BOARDS_EDITOR_LIST_KEY) ?? null,
          dp: this.splitView.size.getSync(DP_KEY) ?? null,
        },
      }),
      knownBoard: (id) => this.boards.boards().some((board) => board.id === id && !board.deleted),
      openBoard: (id) => (this.boards.selectBoard(id) ? 'departed' : 'vetoed'),
      openEditor: (id, fullWindow) => (fullWindow ? this.injector.get(BOARD_OPENER)(id) : this.editBoard(id)),
      closeEditor: () => this.editorStore.closeEditorOutcome(),
      closeItem: () => (this.boards.selectBoard(null) ? 'departed' : 'vetoed'),
      setPanelOpen: (panel, open) => {
        if (panel === 'rail') {
          this.setRailOpen(open);
          return;
        }
        if (panel === 'editorList') this.editorListOpen.set(open);
        if (open) this.splitView.reopen(keyOf(panel), magnetic(panel));
        else {
          this.splitView.collapsed.set(true, keyOf(panel));
          this.splitView.emitSizeChanges();
        }
        if (panel === 'dp' && open) this.fullWindow.reopenDp();
      },
      resizePanel: (panel, width) => {
        if (panel === 'rail') return; // a fixed column, as in every app; the session refuses it first
        this.splitView.size.set(width, keyOf(panel));
        this.splitView.collapsed.set(width < magnetic(panel), keyOf(panel));
        this.splitView.emitSizeChanges();
      },
      openInModal: (id) => {
        const board = this.boards.boards().find((item) => item.id === id);
        if (board) void this.injector.get(BoardsWindowService).openBoardWindow(board, this.injector);
      },
      setFourthPanelApp: (app, boardId) => {
        const panel = this.injector.get(DynamicPanelService, null);
        if (!panel || !(Object.values(Application) as string[]).includes(app)) return false;
        if (app === Application.boards && boardId) return panel.openInBoards({ kind: 'board', id: boardId });
        panel.openApp(app as Application);
        return true;
      },
      setFullWindow: (on) => {
        if (on) return this.fullWindow.begin(this.app);
        this.endFullWindow();
        return true;
      },
      openTab: (id, tab) => this.itemTabs.show(id, tab),
      // The item menu's Add comment (board-context-menu.component.ts addComment): flush the space, then the ONE hand-off to the Comments tab.
      addComment: async (id) => {
        const spaceId = this.spaceContext.contextId();
        const spaceType = this.spaceContext.type();
        if (!(await this.contentFlush.flush(undefined, this.spaceContext))) return 'save_failed';
        if (this.spaceContext.contextId() !== spaceId || this.spaceContext.type() !== spaceType) return 'superseded';
        const reason = await this.handoff.handOffWithReason({ kind: 'board', id, intent: 'record', tab: 'comments', spaceId, spaceType });
        return reason === 'shown' ? 'departed' : reason === 'withdrawn' ? 'superseded' : reason;
      },
      present: (id, frameId) => this.itemTabs.present(id, frameId),
      endPresentation: (id) => this.itemTabs.endPresentation(id),
      setInlineKnowledge: (visible) => this.injector.get(BoardKnowledgeVisibilityService).set(ApplicationScope.MAIN, visible),
    };
  }
}
